import type { ColorAdjust } from './color-adjust-core';
import type { CubeLut } from './image-color-grade';
import type { NormalizedImagePicture } from './image-picture';

export interface ColorAdjustWorkerRequest {
  id: number;
  imageData: ImageData;
  colorAdj?: ColorAdjust | null;
  lutId?: number;
  chroma?: NormalizedImagePicture['chroma'];
}

export type ColorAdjustWorkerResponse =
  | { id: number; imageData: ImageData; error?: never }
  | { id: number; error: string; imageData?: never };

interface Job {
  request: ColorAdjustWorkerRequest;
  resolve: (imageData: ImageData) => void;
  reject: (error: unknown) => void;
  cleanup: () => void;
}

/** 单线程队列限制图片缓冲区的并行数量；取消正在运行的预览会真正终止计算。 */
export class ColorAdjustWorkerQueue {
  private worker: Worker | null = null;
  private active: Job | null = null;
  private pending: Job[] = [];
  private sequence = 0;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private lutSequence = 0;
  private lutIds = new WeakMap<CubeLut, number>();
  private cachedLuts = new Set<number>();

  constructor(private createWorker: () => Worker, private idleMs = 10_000) {}

  process(imageData: ImageData, colorAdj?: ColorAdjust | null, signal?: AbortSignal, chroma?: NormalizedImagePicture['chroma']): Promise<ImageData> {
    if (signal?.aborted) return Promise.reject(signal.reason);
    return new Promise((resolve, reject) => {
      const job: Job = {
        request: { id: ++this.sequence, imageData, colorAdj, chroma },
        resolve,
        reject,
        cleanup: () => signal?.removeEventListener('abort', abort),
      };
      const abort = () => {
        if (this.active === job) {
          this.stopWorker();
          this.active = null;
        } else {
          this.pending = this.pending.filter(candidate => candidate !== job);
        }
        job.cleanup();
        reject(signal?.reason);
        this.pump();
      };
      signal?.addEventListener('abort', abort, { once: true });
      this.pending.push(job);
      this.pump();
    });
  }

  private stopWorker() {
    this.cachedLuts.clear();
    if (this.idleTimer !== null) clearTimeout(this.idleTimer);
    this.idleTimer = null;
    if (this.worker) {
      this.worker.onmessage = null;
      this.worker.onerror = null;
      this.worker.onmessageerror = null;
      this.worker.terminate();
      this.worker = null;
    }
  }

  private finish(error?: Error, result?: ImageData) {
    const job = this.active;
    this.active = null;
    if (job) {
      job.cleanup();
      if (error) job.reject(error);
      else job.resolve(result!);
    }
    this.pump();
  }

  private pump() {
    if (this.idleTimer !== null) clearTimeout(this.idleTimer);
    this.idleTimer = null;
    if (this.active) return;
    const job = this.pending.shift();
    if (!job) {
      if (this.worker) this.idleTimer = setTimeout(() => this.stopWorker(), this.idleMs);
      return;
    }
    this.active = job;
    try {
      if (!this.worker) {
        const worker = this.createWorker();
        this.worker = worker;
        worker.onmessage = (event: MessageEvent<ColorAdjustWorkerResponse>) => {
          if (this.worker !== worker || event.data.id !== this.active?.request.id) return;
          const result = event.data;
          this.finish(result.error !== undefined ? new Error(result.error) : undefined, result.imageData);
        };
        const fail = () => {
          if (this.worker !== worker) return;
          this.stopWorker();
          this.finish(new Error('Color adjustment worker failed'));
        };
        worker.onerror = fail;
        worker.onmessageerror = fail;
      }
      let request = job.request;
      const lut = request.colorAdj?.grade?.lut;
      if (lut) {
        let lutId = this.lutIds.get(lut);
        if (lutId === undefined) { lutId = ++this.lutSequence; this.lutIds.set(lut, lutId); }
        // 最大 65³ LUT 只克隆一次；后续拖动发送参数和引用，避免每帧复制数 MB 格点。
        request = { ...request, lutId,
          ...(this.cachedLuts.has(lutId) ? { colorAdj: { ...request.colorAdj, grade: { ...request.colorAdj!.grade, lut: null } } } : {}),
        };
        this.cachedLuts.delete(lutId);
        this.cachedLuts.add(lutId);
        if (this.cachedLuts.size > 4) this.cachedLuts.delete(this.cachedLuts.values().next().value!);
      }
      this.worker.postMessage(request, [job.request.imageData.data.buffer]);
    } catch (error) {
      this.stopWorker();
      this.finish(error instanceof Error ? error : new Error(String(error)));
    }
  }
}
